import {reaction} from "mobx";
import Data from "../../main/data/index.js";
import Cookies from "js-cookie";
import {accessTokenUuid, accountUuid} from "../../main/data/model/index.js";
import {waitUntil} from "../../main/utils/async.js";
import ExtensionComm from "./extension-comm.js";
import Integration from "../integration/index.js";

const sessionCookieName = "PowerNotesSession";
interface session {
	disabled:     boolean;
	accountUuid?: accountUuid;
	accessToken?: accessTokenUuid;
}

class Session {
	startDisabled = false;

	cookies = Cookies.withConverter({
		write: Session.writeCookie,
		read:  Session.readCookie
	});

	async initIntegration() {
		let extensionIsPresent = false;
		try {
			await ExtensionComm.getCredsFromExtension();
			extensionIsPresent = true;
		} catch (e) {
			// Happy case
		}
		if (extensionIsPresent)
			throw new Error("PN extension found; integration won't initialize");

		window.addEventListener("pnEnable", this.enable.bind(this));
		window.addEventListener("pnDisable", this.disable.bind(this));

		const session = this.cookies.get(sessionCookieName) as session | undefined;
		if (session !== undefined) {
			if (session.disabled)
				this.startDisabled = true;
			else
				await this.login(session);
		}

		reaction(() => ({
			accountUuid: Data.app.accountUuid,
			accessToken: Data.app.accessToken
		}), (data: {
			accountUuid: accountUuid | null;
			accessToken: accessTokenUuid | null;
		}) => {
			if (!data.accessToken || !data.accountUuid)
				return;

			this.startDisabled = false;
			this.cookies.set(sessionCookieName, data as session);
		});
	}

	async login(session: session): Promise<void> {
		if (!session.accountUuid || !session.accessToken)
			return;

		Data.app.isLoggingIn = true;
		Data.app.login(session.accountUuid, session.accessToken);
		if (Integration.isIntegration()) {
			await waitUntil(() => Data.project.all.length > 0 || !Data.app.isLoggedIn);
			if (!Data.app.isLoggedIn) {
				this.clear();
				return;
			}
			await Data.app.selectAndSetCurrentProject();
		}
		Data.app.isLoggingIn = false;
	}

	clear() {
		this.cookies.remove(sessionCookieName);
	}

	disable() {
		this.startDisabled = true;

		const data: session = {disabled: true};
		if (Data.app.isLoggedIn) {
			data.accountUuid = Data.app.accountUuid!;
			data.accessToken = Data.app.accessToken!;
		}
		this.cookies.set(sessionCookieName, data);
	}

	enable() {
		this.startDisabled = false;

		const session = this.cookies.get(sessionCookieName) as session | undefined;
		if (session) {
			if (session.disabled) {
				session.disabled = false;
				this.cookies.set(sessionCookieName, session);
			}

			this.login(session);
		}
	}

	static readCookie = (value: string, name: string): session | string => {
		if (name === sessionCookieName) {
			const parts = value.split(":");
			const data: session = {disabled: false};

			if (parts[0] !== "PNv1")
				throw new Error("Error decoding session cookie");

			if (parts.length === 1)
				return data;

			switch (parts.length) {
				case 2:
					if (parts[1] === "disabled")
						data.disabled = true;
					else
						throw new Error("Error decoding session cookie");
					break;
				case 3:
					data.accountUuid = parts[1] as accountUuid;
					data.accessToken = parts[2] as accessTokenUuid;
					break;
				case 4:
					if (parts[1] === "disabled")
						data.disabled = true;
					else
						throw new Error("Error decoding session cookie");
					data.accountUuid = parts[2] as accountUuid;
					data.accessToken = parts[3] as accessTokenUuid;
					break;
			}

			return data;
		} else
			return Cookies.converter.read(value, name);
	};

	static writeCookie = (value: session | string, name: string): string => {
		if (name === sessionCookieName) {
			let ret = "PNv1";
			const value2 = value as session;
			if (value2.disabled)
				ret += ":disabled";
			if (value2.accountUuid && value2.accessToken)
				ret += `:${value2.accountUuid}:${value2.accessToken}`;
			return ret;
		} else
			return Cookies.converter.write(value as string, name);
	};
}

export default new Session();
